/**
 * Tool registry — single source of truth for the /tools hub. Each entry drives:
 *   • Hub card rendering
 *   • SEO meta tags (title, description, H1)
 *   • Breadcrumb labels
 *   • Internal-link "related tools" + "related guides" rails
 *   • HowTo + ItemList JSON-LD schema on every /tools/{slug} page
 */

// Friendly labels for Learn articles referenced by tools (used in the
// "Related guides" sidebar so we don't have to fetch CMS just to render
// internal links). Add a row here when a new Learn article is published.
export const LEARN_LINK_LABELS = {
    "what-to-negotiate-after-home-inspection":
        "What to negotiate after a home inspection",
    "home-inspection-repair-costs":
        "Home inspection repair costs (typical ranges)",
    "negotiation-after-inspection":
        "How to negotiate after the inspection report",
    "foundation-cracks-inspection":
        "Foundation cracks in inspection reports",
    "water-intrusion-inspection":
        "Water intrusion findings explained",
    "roof-issues-home-inspection":
        "Roof issues commonly flagged in inspections",
    "hvac-issues-home-inspection":
        "HVAC issues to watch for in your inspection",
    "electrical-issues-home-inspection":
        "Electrical issues in home inspection reports",
};

// Default 3-step HowTo template used when a tool doesn't define its own.
// Steps are calm, buyer-first, and end on "use the result in your decision".
const DEFAULT_HOW_TO = (tool) => [
    {
        name: `Open the ${tool.name}`,
        text: `Open the ${tool.name} on Buyer's Leverage and read the short intro so you know which inputs to gather from your inspection report.`,
    },
    {
        name: "Fill in the inspection details",
        text: "Answer the prompts using what your inspector wrote. There are no wrong answers — pick the option closest to what you observed.",
    },
    {
        name: "Review the calm result",
        text: "Read the planning range or concern level, the short reasoning, and the suggested next step. Nothing is filed or shared — it's just for your decision-making.",
    },
];

export const TOOLS = [
    {
        slug: "home-inspection-negotiation-calculator",
        name: "Home Inspection Negotiation Calculator",
        short: "Estimate which inspection issues may matter most in negotiation.",
        samplePreview: "Focus level: HIGH — 3 must-negotiate items, est. $6,200 leverage.",
        time: "2–3 minutes",
        h1: "Home Inspection Negotiation Calculator",
        metaTitle:
            "Home Inspection Negotiation Calculator — Buyer's Leverage",
        metaDescription:
            "Estimate negotiation focus level from inspection issues. Free, rule-based, buyer-first. Upload your report for a full Closing Strategy.",
        keywords: [
            "home inspection negotiation calculator",
            "what to negotiate after home inspection",
            "seller credit after inspection calculator",
        ],
        category: "negotiation",
        relatedTools: [
            "seller-credit-estimator",
            "repair-exposure-calculator",
            "inspection-contingency-timeline-planner",
        ],
        relatedArticles: [
            "what-to-negotiate-after-home-inspection",
            "home-inspection-repair-costs",
        ],
        howToSteps: [
            {
                name: "List the inspection issues",
                text: "Open your inspection report and pick the categories the inspector flagged — roof, electrical, water, foundation, HVAC, plumbing, structural, or general safety.",
            },
            {
                name: "Pick a severity for each",
                text: "Choose the severity tone the inspector used (minor, moderate, major) and whether they recommended specialist follow-up.",
            },
            {
                name: "Review your negotiation focus level",
                text: "The calculator returns a calm focus level (low / moderate / high) and a suggested negotiation framing. Use it to prep your conversation with your agent.",
            },
        ],
    },
    {
        slug: "repair-exposure-calculator",
        name: "Repair Exposure Calculator",
        short: "See a rough planning range for professional repair exposure.",
        samplePreview: "Planning range: $4,800–$11,200 across 5 flagged categories (Asheville, NC).",
        time: "2–3 minutes",
        h1: "Repair Exposure Calculator",
        metaTitle:
            "Repair Exposure Calculator — Home Inspection Repair Cost Estimate",
        metaDescription:
            "Rule-based planning ranges for home inspection repairs. Free tool. Upload your inspection for a report-specific analysis.",
        keywords: [
            "home inspection repair cost calculator",
            "repair exposure calculator",
            "inspection repair estimate",
        ],
        category: "costs",
        relatedTools: [
            "roof-repair-cost-calculator",
            "closing-cost-calculator",
            "seller-credit-estimator",
        ],
        relatedArticles: ["home-inspection-repair-costs"],
        howToSteps: [
            {
                name: "Enter the flagged categories",
                text: "Select each system the inspector flagged (roof, plumbing, electrical, HVAC, foundation, water, etc.) and the severity wording they used.",
            },
            {
                name: "Add your ZIP code",
                text: "Enter the property ZIP so the planning range reflects localized labor and material multipliers from our cost engine.",
            },
            {
                name: "Use the range in your decision",
                text: "Review the low / high planning range and the contributing issues. This is professional-quote planning math — not a bid.",
            },
        ],
    },
    {
        slug: "should-i-buy-this-house-inspection-quiz",
        name: "Should I Buy This House? Inspection Risk Quiz",
        short: "Think through whether inspection findings suggest low, moderate, or high concern.",
        samplePreview: "Concern level: MODERATE — 2 high-cost flags, 1 specialist follow-up advised.",
        time: "2–3 minutes",
        h1: "Should I Buy This House? Inspection Risk Quiz",
        metaTitle:
            "Should I Buy This House? Inspection Risk Quiz — Buyer's Leverage",
        metaDescription:
            "Rule-based concern level for inspection red flags. Educational, not buy/don't-buy advice. Upload your report for buyer risk analysis.",
        keywords: [
            "should I buy a house with inspection issues",
            "home inspection red flags",
            "home inspection risk quiz",
        ],
        category: "decision",
        relatedTools: [
            "home-inspection-negotiation-calculator",
            "repair-exposure-calculator",
            "foundation-crack-severity-checker",
        ],
        relatedArticles: ["negotiation-after-inspection"],
        howToSteps: [
            {
                name: "Answer the red-flag prompts",
                text: "Go through each prompt about what your inspector reported — safety hazards, structural concerns, water intrusion, electrical issues, age of major systems.",
            },
            {
                name: "Get your concern level",
                text: "The quiz returns a calm low / moderate / high concern level with a short rationale. It is decision support, never a buy / don't-buy verdict.",
            },
            {
                name: "Bring the rationale to your agent",
                text: "Use the concern level and reasoning as a starting point for the conversation about negotiation, walk-away, or repair credit.",
            },
        ],
    },
    {
        slug: "seller-credit-estimator",
        name: "Seller Credit Estimator",
        short: "Frame a seller credit discussion range based on repair exposure.",
        samplePreview: "Reasonable seller-credit discussion range: $3,500 – $7,000.",
        time: "3 minutes",
        h1: "Seller Credit Estimator",
        metaTitle:
            "Seller Credit Estimator — How Much to Ask For After Inspection",
        metaDescription:
            "Rule-based seller credit discussion range. Respects lender concession caps. Free tool — upload your report for a full Closing Strategy.",
        keywords: [
            "seller credit estimator",
            "home inspection seller credit calculator",
            "how much seller credit to ask for after inspection",
        ],
        category: "negotiation",
        relatedTools: [
            "home-inspection-negotiation-calculator",
            "closing-cost-calculator",
            "repair-exposure-calculator",
        ],
        relatedArticles: ["negotiation-after-inspection"],
        howToSteps: [
            {
                name: "Enter your purchase price and loan type",
                text: "Add the home price and loan type (conventional, FHA, VA, cash). The estimator respects each loan type's seller-concession cap.",
            },
            {
                name: "Add your repair exposure",
                text: "Drop in the planning range from your Repair Exposure result, or estimate it from the inspection report.",
            },
            {
                name: "Review the discussion range",
                text: "The estimator returns a credit range clipped by lender concession caps. Use it to anchor — not lock — your negotiation.",
            },
        ],
    },
    {
        slug: "foundation-crack-severity-checker",
        name: "Foundation Crack Severity Checker",
        short: "Understand whether a foundation crack may be low, moderate, or high concern.",
        samplePreview: "Severity: MODERATE — hairline horizontal w/ moisture stain. Have a structural engineer evaluate.",
        time: "2 minutes",
        h1: "Foundation Crack Severity Checker",
        metaTitle:
            "Foundation Crack Severity Checker — Stair Step, Vertical, Horizontal",
        metaDescription:
            "Rule-based severity check for foundation cracks. Not engineering advice. Upload your inspection for report-specific foundation analysis.",
        keywords: [
            "foundation crack severity checker",
            "stair step crack foundation",
            "horizontal foundation crack",
        ],
        category: "specialty",
        relatedTools: [
            "water-intrusion-risk-checker",
            "repair-exposure-calculator",
            "should-i-buy-this-house-inspection-quiz",
        ],
        relatedArticles: ["foundation-cracks-inspection"],
        howToSteps: [
            {
                name: "Describe the crack",
                text: "Pick crack pattern (vertical, horizontal, stair-step, diagonal), width, length, and whether moisture or displacement is present.",
            },
            {
                name: "Add context",
                text: "Note whether the crack is interior or exterior, and which floor of the foundation it's on. The inspector's wording matters.",
            },
            {
                name: "Review the concern level",
                text: "Get a calm low / moderate / high severity read and a suggested next step — including when a structural engineer is warranted.",
            },
        ],
    },
    {
        slug: "water-intrusion-risk-checker",
        name: "Water Intrusion Risk Checker",
        short: "Assess water intrusion or moisture concern level.",
        samplePreview: "Risk: HIGH — active moisture + musty odor. Likely source: grading/downspouts.",
        time: "2 minutes",
        h1: "Water Intrusion Risk Checker",
        metaTitle:
            "Water Intrusion Risk Checker — Basement & Crawlspace Inspection",
        metaDescription:
            "Rule-based moisture concern check. Identifies likely sources & specialists. Upload your inspection for full moisture analysis.",
        keywords: [
            "water intrusion risk checker",
            "basement water inspection",
            "efflorescence basement wall",
        ],
        category: "specialty",
        relatedTools: [
            "foundation-crack-severity-checker",
            "roof-repair-cost-calculator",
            "repair-exposure-calculator",
        ],
        relatedArticles: ["water-intrusion-inspection"],
        howToSteps: [
            {
                name: "Pick the signs you saw",
                text: "Select observed signs — efflorescence, staining, active drips, musty smell, sump pump issues, grading problems, gutter overflow.",
            },
            {
                name: "Note the location and history",
                text: "Mark where moisture appears (basement, crawlspace, walls, ceiling) and what the inspector said about prior repairs or active issues.",
            },
            {
                name: "Read the source-and-specialist breakdown",
                text: "Get a concern level plus likely sources and which specialist (waterproofing, plumber, roofer) to call for the right scope.",
            },
        ],
    },
    {
        slug: "roof-repair-cost-calculator",
        name: "Roof Repair Cost Calculator",
        short: "Estimate rough roof repair or replacement exposure.",
        samplePreview: "Planning range: $1,800 – $14,500 depending on patch vs. partial replacement.",
        time: "2 minutes",
        h1: "Roof Repair Cost Calculator",
        metaTitle:
            "Roof Repair Cost Calculator — Repair vs. Replacement After Inspection",
        metaDescription:
            "Rule-based roof planning range. Free buyer tool. Upload your inspection for report-specific roof analysis.",
        keywords: [
            "roof repair cost calculator",
            "roof replacement after inspection",
        ],
        category: "specialty",
        relatedTools: [
            "repair-exposure-calculator",
            "water-intrusion-risk-checker",
            "seller-credit-estimator",
        ],
        relatedArticles: ["roof-issues-home-inspection"],
        howToSteps: [
            {
                name: "Enter the roof basics",
                text: "Add approximate square footage, material (asphalt, metal, tile), age, and any flagged issues like missing shingles, flashing damage, or active leaks.",
            },
            {
                name: "Choose repair scope",
                text: "Pick whether you're estimating spot repair, partial section replacement, or full replacement so the planning range matches the scope.",
            },
            {
                name: "Read your planning range",
                text: "Get a low / high planning range adjusted for your ZIP. Use it to right-size a contractor quote conversation.",
            },
        ],
    },
    {
        slug: "hvac-age-replacement-checker",
        name: "HVAC Age Replacement Checker",
        short: "Understand whether HVAC age & condition suggests service, repair, or replacement.",
        samplePreview: "System recommendation: SERVICE NOW, plan replacement within 3–5 years.",
        time: "2 minutes",
        h1: "HVAC Age Replacement Checker",
        metaTitle:
            "HVAC Age Replacement Checker — Furnace, AC, Heat Pump",
        metaDescription:
            "Rule-based HVAC replacement likelihood from age & inspection issues. Free tool — upload your inspection for full HVAC analysis.",
        keywords: [
            "HVAC age replacement checker",
            "furnace age replacement",
            "AC unit age home inspection",
        ],
        category: "specialty",
        relatedTools: [
            "repair-exposure-calculator",
            "electrical-panel-concern-checker",
            "should-i-buy-this-house-inspection-quiz",
        ],
        relatedArticles: ["hvac-issues-home-inspection"],
        howToSteps: [
            {
                name: "Enter the unit type and age",
                text: "Pick furnace, AC, heat pump, or boiler — and enter the unit's age (from the data plate or your inspector's notes).",
            },
            {
                name: "Add the inspection findings",
                text: "Select any issues the inspector flagged — recent repairs, refrigerant leaks, rust, abnormal noises, deferred service.",
            },
            {
                name: "Get the replacement likelihood",
                text: "Read the calm verdict (service / repair / plan for replacement) and the rationale to bring into your buyer conversation.",
            },
        ],
    },
    {
        slug: "electrical-panel-concern-checker",
        name: "Electrical Panel Concern Checker",
        short: "Identify when electrical panel issues may require electrician follow-up.",
        samplePreview: "Panel concern: HIGH — FPE/Stab-Lok panel detected. Get a licensed electrician.",
        time: "2 minutes",
        h1: "Electrical Panel Concern Checker",
        metaTitle:
            "Electrical Panel Concern Checker — Federal Pacific, Zinsco, More",
        metaDescription:
            "Rule-based electrical concern level. Not safety advice. Upload your inspection for full electrical concern analysis.",
        keywords: [
            "electrical panel concern checker",
            "Federal Pacific panel inspection",
            "double tapped breaker inspection",
        ],
        category: "specialty",
        relatedTools: [
            "hvac-age-replacement-checker",
            "repair-exposure-calculator",
            "should-i-buy-this-house-inspection-quiz",
        ],
        relatedArticles: ["electrical-issues-home-inspection"],
        howToSteps: [
            {
                name: "Identify the panel brand",
                text: "Pick the panel brand (Federal Pacific, Zinsco, Square D, GE, Siemens, other) — your inspector usually notes this.",
            },
            {
                name: "Mark the flagged issues",
                text: "Select what the inspector wrote — double-tapped breakers, aluminum wiring, missing knockouts, corrosion, exposed wiring.",
            },
            {
                name: "Read the concern level",
                text: "Get a calm concern level plus suggested electrician follow-up scope. Brand-specific panels (FPE, Zinsco) get extra context.",
            },
        ],
    },
    {
        slug: "inspection-contingency-timeline-planner",
        name: "Inspection Contingency Timeline Planner",
        short: "Plan what to do before the inspection contingency deadline.",
        samplePreview: "Due dates: Inspection 4/12 · Objection 4/14 · Resolution 4/19 · Removal 4/21.",
        time: "2 minutes",
        h1: "Inspection Contingency Timeline Planner",
        metaTitle:
            "Inspection Contingency Timeline Planner — Step-by-Step Deadline Checklist",
        metaDescription:
            "Free rule-based planner for inspection contingency periods. Decide credit, repair, or price reduction before the deadline.",
        keywords: [
            "inspection contingency timeline",
            "home inspection deadline planner",
            "what to do during inspection contingency",
        ],
        category: "negotiation",
        relatedTools: [
            "home-inspection-negotiation-calculator",
            "seller-credit-estimator",
            "repair-exposure-calculator",
        ],
        relatedArticles: [
            "negotiation-after-inspection",
            "what-to-negotiate-after-home-inspection",
        ],
        howToSteps: [
            {
                name: "Enter your contingency window",
                text: "Add the inspection contingency deadline from your contract — the planner counts the days backward from there.",
            },
            {
                name: "Mark where you are today",
                text: "Note whether the inspection is scheduled, in progress, or complete — and whether you've received specialist follow-up bids.",
            },
            {
                name: "Get your day-by-day checklist",
                text: "The planner returns the calm sequence: review report, get bids, draft response, deliver, allow seller response, decide before the deadline.",
            },
        ],
    },
    {
        slug: "closing-cost-calculator",
        name: "Closing Cost Calculator",
        short: "Estimate buyer closing costs by loan type, points, and seller credit.",
        samplePreview: "Estimated total: $9,840 (Lender ~$1,200 · Title $2,100 · Pre-paids $6,540).",
        time: "2 minutes",
        h1: "Closing Cost Calculator",
        metaTitle:
            "Closing Cost Calculator — How Much Will I Pay at Closing?",
        metaDescription:
            "Free rule-based estimate of buyer closing costs. Adjusts for FHA/VA/Conventional/cash, seller credits, points, and prepaid escrow.",
        keywords: [
            "closing cost calculator",
            "how much are buyer closing costs",
            "fha closing costs estimate",
        ],
        category: "costs",
        relatedTools: [
            "seller-credit-estimator",
            "repair-exposure-calculator",
            "home-inspection-negotiation-calculator",
        ],
        relatedArticles: [
            "home-inspection-repair-costs",
        ],
        howToSteps: [
            {
                name: "Enter your home price and loan",
                text: "Add the home price, loan type (conventional, FHA, VA, cash) and whether you're buying points.",
            },
            {
                name: "Add seller credit and escrow toggle",
                text: "Drop in any seller credit % you're negotiating, and choose whether to include prepaid escrow (taxes + insurance reserves).",
            },
            {
                name: "Review your estimate",
                text: "Get a calm closing-cost estimate respecting your loan type's concession cap. Use it for budgeting — your lender's LE will be the exact figure.",
            },
        ],
    },
];

export function getTool(slug) {
    return TOOLS.find((t) => t.slug === slug) || null;
}

export function getRelatedTools(tool, limit = 3) {
    if (!tool) return [];
    const explicit = (tool.relatedTools || [])
        .map((s) => getTool(s))
        .filter(Boolean);
    if (explicit.length >= limit) return explicit.slice(0, limit);
    // Fallback: fill from same category
    const filler = TOOLS.filter(
        (t) =>
            t.slug !== tool.slug &&
            t.category === tool.category &&
            !explicit.find((e) => e.slug === t.slug),
    );
    return [...explicit, ...filler].slice(0, limit);
}

export function getHowToSteps(tool) {
    if (tool?.howToSteps?.length) return tool.howToSteps;
    return DEFAULT_HOW_TO(tool || { name: "tool" });
}
